/**
 * Track B — LightBurn .lbrn/.lbrn2 project parsing.
 * See docs/recon/lbrn-parsing.json + docs/recon/lightburn-format.json.
 * Zip-by-magic detection, namespace-agnostic walk, case-insensitive attrs,
 * legacy V/P and compact VertList/PrimList (+ VertID/PrimID reuse cache),
 * XForm composition through groups, Text via BackupPath only.
 *
 * Ships a small dependency-free XML parser/serializer (used for both reading
 * and, from lbrnExport, faithful write-back) so everything is unit-testable in
 * node as well as the browser.
 */
import type {
  ImportWarning,
  LayerInfo,
  Mat,
  PolyGeom,
  VectorDocument,
} from "../core/types";
import { IDENTITY, LIGHTBURN_PALETTE } from "../core/types";
import {
  applyMat,
  ellipseRing,
  flattenCubic,
  matMul,
  parseMatText,
  rectRing,
  regularPolygonRing,
} from "../core/geometry";
import { isZip, readZipEntries } from "./zip";

// ---------------------------------------------------------------------------
// Minimal XML tree (tag / attrs / mixed children). Attribute values and text
// are entity-decoded on read and re-escaped on serialize. Comments/PIs/DOCTYPE
// are skipped (matching the reference reader); unknown ELEMENTS survive.
// ---------------------------------------------------------------------------

export interface XmlTextNode {
  kind: "text";
  text: string;
}

export type XmlNode = XmlElement | XmlTextNode;

export class XmlElement {
  readonly kind = "element";
  tag: string;
  attrs: { name: string; value: string }[] = [];
  children: XmlNode[] = [];
  private lc: Map<string, string> | null = null;

  constructor(tag: string) {
    this.tag = tag;
  }

  get localName(): string {
    let t = this.tag;
    const brace = t.indexOf("}");
    if (brace >= 0) t = t.slice(brace + 1);
    const colon = t.indexOf(":");
    if (colon >= 0) t = t.slice(colon + 1);
    return t;
  }

  private lcMap(): Map<string, string> {
    if (!this.lc) {
      this.lc = new Map();
      for (const a of this.attrs) {
        const key = a.name.toLowerCase();
        if (!this.lc.has(key)) this.lc.set(key, a.value);
      }
    }
    return this.lc;
  }

  /** Case-insensitive, multi-spelling attribute lookup (exact match wins per name). */
  attr(...names: string[]): string | null {
    const map = this.lcMap();
    for (const nm of names) {
      for (const a of this.attrs) if (a.name === nm) return a.value;
      const v = map.get(nm.toLowerCase());
      if (v !== undefined) return v;
    }
    return null;
  }

  setAttr(name: string, value: string): void {
    for (const a of this.attrs) {
      if (a.name === name) {
        a.value = value;
        this.lc = null;
        return;
      }
    }
    this.attrs.push({ name, value });
    this.lc = null;
  }

  append(node: XmlNode): void {
    this.children.push(node);
  }

  setText(text: string): void {
    this.children = [{ kind: "text", text }];
  }
}

function isSpace(ch: string): boolean {
  return ch === " " || ch === "\t" || ch === "\n" || ch === "\r" || ch === "\f" || ch === "\v";
}

function decodeEntities(s: string): string {
  if (s.indexOf("&") < 0) return s;
  return s.replace(/&(#x[0-9A-Fa-f]+|#\d+|[A-Za-z][A-Za-z0-9]*);/g, (full: string, ent: string) => {
    if (ent[0] === "#") {
      const code = ent[1] === "x" || ent[1] === "X" ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
      if (Number.isFinite(code) && code >= 0 && code <= 0x10ffff) {
        try {
          return String.fromCodePoint(code);
        } catch {
          return full;
        }
      }
      return full;
    }
    switch (ent) {
      case "amp":
        return "&";
      case "lt":
        return "<";
      case "gt":
        return ">";
      case "quot":
        return '"';
      case "apos":
        return "'";
      default:
        return full;
    }
  });
}

function escapeXmlText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeXmlAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function findTagEnd(s: string, start: number): number {
  let i = start + 1;
  let quote = "";
  const n = s.length;
  while (i < n) {
    const ch = s[i];
    if (quote) {
      if (ch === quote) quote = "";
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === ">") {
      return i;
    }
    i++;
  }
  return -1;
}

function parseStartTag(inner: string): XmlElement {
  const n = inner.length;
  let i = 0;
  while (i < n && isSpace(inner[i])) i++;
  const nameStart = i;
  while (i < n && !isSpace(inner[i]) && inner[i] !== "/") i++;
  const el = new XmlElement(inner.slice(nameStart, i));
  while (i < n) {
    while (i < n && (isSpace(inner[i]) || inner[i] === "/")) i++;
    if (i >= n) break;
    const attrNameStart = i;
    while (i < n && inner[i] !== "=" && !isSpace(inner[i]) && inner[i] !== "/") i++;
    const attrName = inner.slice(attrNameStart, i);
    if (!attrName) break;
    while (i < n && isSpace(inner[i])) i++;
    let value = "";
    if (inner[i] === "=") {
      i++;
      while (i < n && isSpace(inner[i])) i++;
      const q = inner[i];
      if (q === '"' || q === "'") {
        i++;
        const vs = i;
        while (i < n && inner[i] !== q) i++;
        value = inner.slice(vs, i);
        i++;
      } else {
        const vs = i;
        while (i < n && !isSpace(inner[i]) && inner[i] !== "/") i++;
        value = inner.slice(vs, i);
      }
    }
    el.setAttr(attrName, decodeEntities(value));
  }
  return el;
}

/** Parse an XML string into its root element (namespace-agnostic, tolerant). */
export function parseXml(input: string): XmlElement | null {
  const n = input.length;
  let i = 0;
  const roots: XmlNode[] = [];
  const stack: XmlElement[] = [];
  const push = (node: XmlNode): void => {
    if (stack.length) stack[stack.length - 1].children.push(node);
    else roots.push(node);
  };
  while (i < n) {
    const lt = input.indexOf("<", i);
    if (lt < 0) break;
    if (lt > i && stack.length) {
      const raw = input.slice(i, lt);
      if (raw.trim().length) push({ kind: "text", text: decodeEntities(raw) });
    }
    if (input.startsWith("<!--", lt)) {
      const end = input.indexOf("-->", lt + 4);
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (input.startsWith("<![CDATA[", lt)) {
      const end = input.indexOf("]]>", lt + 9);
      const stop = end < 0 ? n : end;
      if (stack.length) push({ kind: "text", text: input.slice(lt + 9, stop) });
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (input.startsWith("<?", lt)) {
      const end = input.indexOf("?>", lt + 2);
      i = end < 0 ? n : end + 2;
      continue;
    }
    if (input.startsWith("<!", lt)) {
      const end = input.indexOf(">", lt + 2);
      i = end < 0 ? n : end + 1;
      continue;
    }
    if (input.startsWith("</", lt)) {
      const end = input.indexOf(">", lt + 2);
      if (stack.length) stack.pop();
      i = end < 0 ? n : end + 1;
      continue;
    }
    const end = findTagEnd(input, lt);
    if (end < 0) break;
    let tagContent = input.slice(lt + 1, end);
    const selfClose = tagContent.endsWith("/");
    if (selfClose) tagContent = tagContent.slice(0, -1);
    const el = parseStartTag(tagContent);
    push(el);
    if (!selfClose) stack.push(el);
    i = end + 1;
  }
  for (const node of roots) if (node.kind === "element") return node;
  return null;
}

/** Serialize a full document (XML declaration + 2-space indent, self-closing empties). */
export function serializeXml(root: XmlElement): string {
  const out: string[] = [];
  serializeElement(root, 0, out);
  return '<?xml version="1.0" encoding="UTF-8"?>\n' + out.join("\n") + "\n";
}

/** Serialize a single element with no XML declaration (used for fragments/tests). */
export function serializeFragment(el: XmlElement): string {
  const out: string[] = [];
  serializeElement(el, 0, out);
  return out.join("\n");
}

function serializeElement(el: XmlElement, depth: number, out: string[]): void {
  const indent = "  ".repeat(depth);
  let attrs = "";
  for (const a of el.attrs) attrs += ` ${a.name}="${escapeXmlAttr(a.value)}"`;

  let hasElement = false;
  let hasText = false;
  for (const c of el.children) {
    if (c.kind === "element") hasElement = true;
    else if (c.text.length > 0) hasText = true;
  }

  if (!hasElement && !hasText) {
    out.push(`${indent}<${el.tag}${attrs} />`);
    return;
  }
  if (!hasElement) {
    // Pure text node — keep it on a single line (leaf value element).
    let text = "";
    for (const c of el.children) if (c.kind === "text") text += c.text;
    out.push(`${indent}<${el.tag}${attrs}>${escapeXmlText(text)}</${el.tag}>`);
    return;
  }

  // Mixed or element-only content: preserve document order so interleaved text
  // (e.g. LightBurn notes/text between child shapes) is not dropped on copy
  // export. Non-whitespace text nodes are emitted on their own indented line;
  // pure-whitespace nodes (the parser's collapsed formatting) are skipped so
  // re-serialization stays clean and byte-stable for element-only elements.
  out.push(`${indent}<${el.tag}${attrs}>`);
  const childIndent = "  ".repeat(depth + 1);
  for (const c of el.children) {
    if (c.kind === "element") {
      serializeElement(c, depth + 1, out);
    } else if (c.text.trim().length > 0) {
      out.push(`${childIndent}${escapeXmlText(c.text.trim())}`);
    }
  }
  out.push(`${indent}</${el.tag}>`);
}

// ---------------------------------------------------------------------------
// Tree navigation + value resolution (mirrors the reference reader semantics).
// ---------------------------------------------------------------------------

export function childElements(el: XmlElement, tag?: string): XmlElement[] {
  const out: XmlElement[] = [];
  for (const c of el.children) {
    if (c.kind === "element" && (!tag || c.localName === tag)) out.push(c);
  }
  return out;
}

export function firstChild(el: XmlElement, tag: string): XmlElement | null {
  for (const c of el.children) {
    if (c.kind === "element" && c.localName === tag) return c;
  }
  return null;
}

export function elementText(el: XmlElement): string {
  let s = "";
  for (const c of el.children) if (c.kind === "text") s += c.text;
  return s;
}

function getTextOrAttr(el: XmlElement, names: string[], def: string): string {
  const a = el.attr(...names);
  if (a !== null) return a;
  for (const nm of names) {
    const ch = firstChild(el, nm);
    if (ch) {
      const t = elementText(ch).trim();
      if (t) return t;
      const v = ch.attr("Value", "value");
      if (v !== null) return v;
    }
  }
  return def;
}

function getChildText(el: XmlElement, tag: string): string {
  const ch = firstChild(el, tag);
  if (ch) {
    const t = elementText(ch).trim();
    if (t) return t;
  }
  const a = el.attr(tag);
  return a !== null ? a : "";
}

export function parseFloatD(value: string | null | undefined, def = 0): number {
  if (value == null) return def;
  const n = parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : def;
}

export function parseIntD(value: string | null | undefined, def = 0): number {
  if (value == null) return def;
  const n = parseFloat(String(value).trim());
  return Number.isFinite(n) ? Math.trunc(n) : def;
}

function parseCutIndex(el: XmlElement, inherited: number | null): number | null {
  const raw = el.attr("CutIndex", "cutindex", "cutIndex");
  if (raw === null) return inherited;
  const n = parseFloat(raw.trim());
  return Number.isFinite(n) ? Math.trunc(n) : inherited;
}

function parseXForm(el: XmlElement): Mat {
  let raw: string | null = el.attr("XForm", "XFormVal", "xform", "xformval");
  if (raw === null) {
    const ch = firstChild(el, "XForm");
    if (ch) raw = elementText(ch);
  }
  return parseMatText(raw);
}

function hex2(n: number): string {
  return (n & 0xff).toString(16).toUpperCase().padStart(2, "0");
}

function sanitizeColor(raw: string, fallback: string): string {
  const s = (raw || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toUpperCase();
  if (/^[0-9A-Fa-f]{6}$/.test(s)) return "#" + s.toUpperCase();
  // 32-bit packed integer (COLORREF-style low byte = red / "BGR" hex order).
  if (/^-?\d+$/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n)) {
      const u = n >>> 0;
      return "#" + hex2(u) + hex2(u >> 8) + hex2(u >> 16);
    }
  }
  const parts = s.split(/[,\s]+/).filter(Boolean);
  if (parts.length >= 3) {
    const rgb: number[] = [];
    for (const p of parts.slice(0, 3)) {
      const v = parseFloat(p);
      if (!Number.isFinite(v)) return fallback;
      rgb.push(Math.max(0, Math.min(255, Math.trunc(v))));
    }
    return "#" + hex2(rgb[0]) + hex2(rgb[1]) + hex2(rgb[2]);
  }
  return fallback;
}

function pad2(n: number): string {
  return (n < 0 ? "-" : "") + Math.abs(n).toString().padStart(2, "0");
}

function paletteColor(index: number): string {
  return LIGHTBURN_PALETTE[((index % 30) + 30) % 30];
}

// ---------------------------------------------------------------------------
// Compact + legacy path grammars.
// ---------------------------------------------------------------------------

export interface LbrnVertex {
  vx: number;
  vy: number;
  c0x?: number;
  c0y?: number;
  c1x?: number;
  c1y?: number;
}

export type LbrnPrim = ["L" | "B", number, number] | ["LineClosed", -1, -1];

const NUM_RE = /[+\-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+\-]?\d+)?/y;

function numberScan(text: string, start: number): [number | null, number] {
  NUM_RE.lastIndex = start;
  const m = NUM_RE.exec(text);
  if (!m || m.index !== start) return [null, start];
  return [parseFloat(m[0]), start + m[0].length];
}

function setControl(v: LbrnVertex, key: string, num: number): void {
  if (key === "c0x") v.c0x = num;
  else if (key === "c0y") v.c0y = num;
  else if (key === "c1x") v.c1x = num;
  else if (key === "c1y") v.c1y = num;
}

export function parseVertList(text: string): LbrnVertex[] {
  const verts: LbrnVertex[] = [];
  const s = text || "";
  const n = s.length;
  let i = 0;
  while (i < n) {
    if (s[i] !== "V") {
      i++;
      continue;
    }
    i++;
    while (i < n && isSpace(s[i])) i++;
    const xr = numberScan(s, i);
    if (xr[0] === null) {
      i++;
      continue;
    }
    i = xr[1];
    while (i < n && isSpace(s[i])) i++;
    const yr = numberScan(s, i);
    if (yr[0] === null) continue;
    i = yr[1];
    const vert: LbrnVertex = { vx: xr[0], vy: yr[0] };
    while (i < n && s[i] !== "V") {
      if (s.startsWith("c0x", i) || s.startsWith("c0y", i) || s.startsWith("c1x", i) || s.startsWith("c1y", i)) {
        const key = s.substr(i, 3);
        i += 3;
        const nr = numberScan(s, i);
        if (nr[0] !== null) {
          setControl(vert, key, nr[0]);
          i = nr[1];
        } else {
          i += 1;
        }
      } else {
        i += 1;
      }
    }
    verts.push(vert);
  }
  return verts;
}

export function parsePrimList(text: string): LbrnPrim[] {
  const prims: LbrnPrim[] = [];
  if (!text) return prims;
  if (text.trim() === "LineClosed") return [["LineClosed", -1, -1]];
  const re = /([A-Za-z]+)\s*(\d+)\s+(\d+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const letter = m[1][0];
    if (letter === "L" || letter === "B") {
      prims.push([letter, parseInt(m[2], 10), parseInt(m[3], 10)]);
    }
  }
  return prims;
}

// PROTECTED: legacy LightBurn V/P import compat (CLAUDE.md rule 2) — do not remove
function legacyVertices(el: XmlElement): LbrnVertex[] {
  const verts: LbrnVertex[] = [];
  for (const v of childElements(el, "V")) {
    const vert: LbrnVertex = {
      vx: parseFloatD(v.attr("vx", "x"), 0),
      vy: parseFloatD(v.attr("vy", "y"), 0),
    };
    setLegacyControl(vert, "c0x", v.attr("c0x"));
    setLegacyControl(vert, "c0y", v.attr("c0y"));
    setLegacyControl(vert, "c1x", v.attr("c1x"));
    setLegacyControl(vert, "c1y", v.attr("c1y"));
    verts.push(vert);
  }
  return verts;
}

function setLegacyControl(v: LbrnVertex, key: string, raw: string | null): void {
  if (raw === null) return;
  const num = parseFloat(raw.trim());
  if (Number.isFinite(num)) setControl(v, key, num);
}

function legacyPrimitives(el: XmlElement): LbrnPrim[] {
  const prims: LbrnPrim[] = [];
  for (const p of childElements(el, "P")) {
    const raw = (p.attr("T", "type") ?? "L").trim();
    const kind: "L" | "B" = raw[0] === "B" ? "B" : "L";
    prims.push([kind, parseIntD(p.attr("p0"), 0), parseIntD(p.attr("p1"), 0)]);
  }
  return prims;
}

function hasC0(v: LbrnVertex): boolean {
  return v.c0x !== undefined && v.c0y !== undefined;
}
function hasC1(v: LbrnVertex): boolean {
  return v.c1x !== undefined && v.c1y !== undefined;
}

const JOIN_TOL = 0.001;
const CLOSE_EPS = 1e-7;

/**
 * Reconstruct polyline contours from vertices + primitives. Returns flat
 * Float64Array rings (implicitly closed — a coincident trailing vertex is
 * dropped so the output follows the shared Ring convention).
 */
function buildPolylines(
  verts: LbrnVertex[],
  prims: LbrnPrim[],
  mat: Mat,
  steps: number,
): [Float64Array, boolean][] {
  const results: [Float64Array, boolean][] = [];
  if (!verts.length) return results;
  const s = Math.max(3, Math.min(96, Math.trunc(steps) || 16));

  if (prims.length && prims[0][0] === "LineClosed") {
    const flat = transformVerts(verts, mat);
    results.push([flat, verts.length >= 3]);
    return results;
  }
  if (!prims.length) {
    if (verts.length >= 3) results.push([transformVerts(verts, mat), true]);
    return results;
  }

  const tol = 0.4 / s;
  let current: number[] = [];
  let firstIdx = -1;
  let lastIdx = -1;

  const finish = (): void => {
    const nPts = current.length >> 1;
    if (nPts >= 2) {
      const lx = current[current.length - 2];
      const ly = current[current.length - 1];
      const gap = Math.hypot(current[0] - lx, current[1] - ly);
      const closed = gap <= JOIN_TOL;
      let arr = current;
      if (closed && gap <= CLOSE_EPS) arr = current.slice(0, current.length - 2);
      const finalClosed = closed && (arr.length >> 1) >= 3;
      results.push([Float64Array.from(arr), finalClosed]);
    }
    current = [];
    firstIdx = -1;
    lastIdx = -1;
  };

  for (const prim of prims) {
    const kind = prim[0];
    const p0i = prim[1];
    const p1i = prim[2];
    if (kind === "LineClosed") continue;
    if (p0i < 0 || p1i < 0 || p0i >= verts.length || p1i >= verts.length) continue;
    const sv = verts[p0i];
    const ev = verts[p1i];
    const start = applyMat(mat, sv.vx, sv.vy);
    const end = applyMat(mat, ev.vx, ev.vy);

    if (current.length === 0) {
      current.push(start.x, start.y);
      firstIdx = p0i;
    } else {
      const lx = current[current.length - 2];
      const ly = current[current.length - 1];
      if (lastIdx !== p0i && Math.hypot(lx - start.x, ly - start.y) > JOIN_TOL) {
        finish();
        current.push(start.x, start.y);
        firstIdx = p0i;
      }
    }

    if (kind === "B" && hasC0(sv) && hasC1(ev)) {
      const tmp: number[] = [];
      flattenCubic(tmp, sv.vx, sv.vy, sv.c0x!, sv.c0y!, ev.c1x!, ev.c1y!, ev.vx, ev.vy, tol, s);
      for (let k = 0; k < tmp.length; k += 2) {
        const p = applyMat(mat, tmp[k], tmp[k + 1]);
        current.push(p.x, p.y);
      }
    } else {
      current.push(end.x, end.y);
    }
    lastIdx = p1i;

    if (firstIdx >= 0 && lastIdx === firstIdx && (current.length >> 1) >= 3) {
      finish();
    }
  }
  finish();
  return results;
}

function transformVerts(verts: LbrnVertex[], mat: Mat): Float64Array {
  const out = new Float64Array(verts.length * 2);
  for (let k = 0; k < verts.length; k++) {
    const p = applyMat(mat, verts[k].vx, verts[k].vy);
    out[k * 2] = p.x;
    out[k * 2 + 1] = p.y;
  }
  return out;
}

function transformRing(ring: Float64Array, mat: Mat): Float64Array {
  const out = new Float64Array(ring.length);
  for (let i = 0; i < ring.length; i += 2) {
    const p = applyMat(mat, ring[i], ring[i + 1]);
    out[i] = p.x;
    out[i + 1] = p.y;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Layer metadata.
// ---------------------------------------------------------------------------

function countShapes(el: XmlElement, inheritedCut: number | null, counts: Map<number, number>, depth: number): void {
  for (const child of childElements(el, "Shape")) {
    const t = child.attr("Type", "type") ?? "";
    const cut = parseCutIndex(child, inheritedCut);
    if (t === "Group") {
      if (depth >= MAX_NEST_DEPTH) continue;
      const container = firstChild(child, "Children") ?? child;
      countShapes(container, cut, counts, depth + 1);
    } else if (cut !== null && (t === "Path" || t === "Rect" || t === "Ellipse" || t === "Polygon" || t === "Text")) {
      counts.set(cut, (counts.get(cut) ?? 0) + 1);
    }
  }
}

function getLayers(root: XmlElement): LayerInfo[] {
  const counts = new Map<number, number>();
  countShapes(root, null, counts, 0);
  const layers: LayerInfo[] = [];
  const seen = new Set<number>();
  let order = 0;
  for (const cs of childElements(root, "CutSetting")) {
    // Index may be an attribute OR a child <index Value=".."/> (lbrn2 form);
    // fall back to document order when absent (matching the reference reader).
    const idxRaw = getTextOrAttr(cs, ["index", "Index", "idx", "Idx"], "");
    const idx = idxRaw !== "" ? parseIntD(idxRaw, order) : order;
    const fallback = paletteColor(idx);
    const name = getTextOrAttr(cs, ["name", "Name", "label", "Label"], "Layer C" + pad2(idx));
    const color = sanitizeColor(getTextOrAttr(cs, ["color", "Color", "RGB", "rgb"], fallback), fallback);
    layers.push({ index: idx, name, color, shapeCount: counts.get(idx) ?? 0 });
    seen.add(idx);
    order++;
  }
  const extra = [...counts.keys()].filter((k) => !seen.has(k)).sort((a, b) => a - b);
  for (const idx of extra) {
    layers.push({ index: idx, name: "Layer C" + pad2(idx), color: paletteColor(idx), shapeCount: counts.get(idx) ?? 0 });
  }
  layers.sort((a, b) => a.index - b.index);
  return layers;
}

// ---------------------------------------------------------------------------
// Geometry extraction.
// ---------------------------------------------------------------------------

/** Guard against pathologically (or maliciously) deep group nesting. */
const MAX_NEST_DEPTH = 512;

/**
 * Largest source file we will parse. LightBurn projects are XML/zip and a real
 * one is well under this; anything larger is almost certainly not a project and
 * would only risk freezing the tab.
 */
const MAX_LBRN_BYTES = 256 * 1024 * 1024; // 256 MB (decoded text or raw bytes)

function fileTooLargeError(): Error {
  return new Error(
    "This file is larger than 256 MB, which is far bigger than any real LightBurn project. " +
      "It may be the wrong file — pick your .lbrn or .lbrn2 project instead.",
  );
}

function addWarn(map: Map<string, ImportWarning>, kind: "skip" | "warn", message: string): void {
  const ex = map.get(message);
  if (ex) ex.count++;
  else map.set(message, { kind, message, count: 1 });
}

function pathPolylines(
  el: XmlElement,
  mat: Mat,
  steps: number,
  vertCache: Map<string, LbrnVertex[]>,
  primCache: Map<string, LbrnPrim[]>,
  warnMap: Map<string, ImportWarning>,
): [Float64Array, boolean][] {
  const legacyV = childElements(el, "V");
  const vertId = el.attr("VertID", "vertid");
  const primId = el.attr("PrimID", "primid");
  let verts: LbrnVertex[];
  let prims: LbrnPrim[];
  // PROTECTED: legacy LightBurn V/P import compat (CLAUDE.md rule 2) — do not remove
  if (legacyV.length) {
    verts = legacyVertices(el);
    prims = legacyPrimitives(el);
    if (vertId !== null) vertCache.set(vertId, verts);
    if (primId !== null) primCache.set(primId, prims);
  } else {
    const vtext = getChildText(el, "VertList");
    if (vtext) {
      verts = parseVertList(vtext);
      if (vertId !== null) vertCache.set(vertId, verts);
    } else if (vertId !== null && vertCache.has(vertId)) {
      verts = vertCache.get(vertId)!;
    } else {
      verts = [];
    }
    const ptext = getChildText(el, "PrimList");
    if (ptext) {
      prims = parsePrimList(ptext);
      if (primId !== null) primCache.set(primId, prims);
    } else if (primId !== null && primCache.has(primId)) {
      prims = primCache.get(primId)!;
    } else {
      prims = [];
    }
  }
  if (!verts.length) {
    addWarn(warnMap, "skip", "Path with no vertices");
    return [];
  }
  return buildPolylines(verts, prims, mat, steps);
}

function extractGeometryImpl(
  root: XmlElement,
  selectedLayers: ReadonlySet<number>,
  curveSteps: number,
): { geoms: PolyGeom[]; warnings: ImportWarning[] } {
  const steps = Math.max(3, Math.min(96, Math.trunc(curveSteps) || 16));
  const geoms: PolyGeom[] = [];
  const warnMap = new Map<string, ImportWarning>();
  const vertCache = new Map<string, LbrnVertex[]>();
  const primCache = new Map<string, LbrnPrim[]>();

  const emit = (layer: number | null, points: Float64Array, closed: boolean, source: string): void => {
    geoms.push({ layer: layer ?? 0, points, closed, source });
  };

  const extractShape = (el: XmlElement, parentMat: Mat, inheritedCut: number | null, depth: number): void => {
    if (el.localName !== "Shape") return;
    const t = el.attr("Type", "type") ?? "";
    const cut = parseCutIndex(el, inheritedCut);
    const mat = matMul(parentMat, parseXForm(el));
    const selected = cut !== null && selectedLayers.has(cut);

    if (t === "Group") {
      if (depth >= MAX_NEST_DEPTH) {
        addWarn(warnMap, "warn", "Some groups were nested too deeply to import — flatten or ungroup them in LightBurn, then re-open the file");
        return;
      }
      const container = firstChild(el, "Children");
      const host = container ?? el;
      for (const c of childElements(host, "Shape")) extractShape(c, mat, cut, depth + 1);
      return;
    }
    if (t === "Path") {
      const polys = pathPolylines(el, mat, steps, vertCache, primCache, warnMap);
      if (selected) {
        for (const [pts, closed] of polys) if (pts.length >= 4) emit(cut, pts, closed, "Path");
      }
      return;
    }
    if (t === "Rect") {
      if (selected) {
        const w = parseFloatD(el.attr("W", "w"), 0);
        const h = parseFloatD(el.attr("H", "h"), 0);
        const cr = parseFloatD(el.attr("Cr", "CR", "cr"), 0);
        if (w > 0 && h > 0) {
          emit(cut, transformRing(rectRing(w, h, cr, Math.max(3, Math.floor(steps / 3))), mat), true, "Rect");
        } else {
          addWarn(warnMap, "skip", "Rect with missing W/H");
        }
      }
      return;
    }
    if (t === "Ellipse") {
      if (selected) {
        const rx = parseFloatD(el.attr("Rx", "rx"), 0);
        const ry = parseFloatD(el.attr("Ry", "ry"), 0);
        if (rx > 0 && ry > 0) {
          emit(cut, transformRing(ellipseRing(rx, ry, Math.max(24, steps * 4)), mat), true, "Ellipse");
        } else {
          addWarn(warnMap, "skip", "Ellipse with missing Rx/Ry");
        }
      }
      return;
    }
    if (t === "Polygon") {
      if (selected) {
        const rx = parseFloatD(el.attr("Rx", "rx"), 0);
        const ry = parseFloatD(el.attr("Ry", "ry"), 0);
        const sides = parseIntD(el.attr("N", "n"), 3);
        if (rx > 0 && ry > 0 && sides >= 3) {
          emit(cut, transformRing(regularPolygonRing(rx, ry, sides, -Math.PI / 2), mat), true, "Polygon");
        } else {
          addWarn(warnMap, "skip", "Polygon with invalid Rx/Ry/N");
        }
      }
      return;
    }
    if (t === "Text") {
      const backup = firstChild(el, "BackupPath");
      if (backup) {
        const childShapes = childElements(backup, "Shape");
        if (childShapes.length) {
          for (const c of childShapes) extractShape(c, mat, cut, depth + 1);
        } else if (selected) {
          for (const [pts, closed] of pathPolylines(backup, mat, steps, vertCache, primCache, warnMap)) {
            if (pts.length >= 4) emit(cut, pts, closed, "Text");
          }
        }
      } else if (selected) {
        addWarn(warnMap, "warn", "Text without a vector outline skipped — convert it to a path in LightBurn first");
      }
      return;
    }
    if (selected) {
      if (t === "Bitmap" || t === "Image") {
        addWarn(warnMap, "warn", "Image/bitmap layer skipped — raster shapes are not pixelated");
      } else {
        addWarn(warnMap, "skip", (t || "Unknown") + " shape skipped");
      }
    }
  };

  for (const shape of childElements(root, "Shape")) extractShape(shape, IDENTITY, null, 0);
  return { geoms, warnings: [...warnMap.values()] };
}

// ---------------------------------------------------------------------------
// Public entry point.
// ---------------------------------------------------------------------------

function decodeUtf8(bytes: Uint8Array): string {
  const TD = (globalThis as { TextDecoder?: typeof TextDecoder }).TextDecoder;
  if (typeof TD === "function") return new TD("utf-8").decode(bytes);
  // Last-resort fallback (never hit in browser/node): strip BOM, map bytes.
  let start = 0;
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) start = 3;
  let s = "";
  for (let i = start; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return s;
}

/** Parse raw file bytes (plain XML or ZIP container) into a VectorDocument. */
export async function parseLbrn(bytes: Uint8Array, fileName: string): Promise<VectorDocument> {
  if (bytes.length > MAX_LBRN_BYTES) throw fileTooLargeError();
  let xmlBytes = bytes;
  if (isZip(bytes)) {
    const entries = readZipEntries(bytes);
    if (entries.length) {
      const preferred = entries.find((e) => /\.(lbrn2?|xml)$/i.test(e.name)) ?? entries[0];
      try {
        xmlBytes = await preferred.data();
      } catch {
        xmlBytes = bytes;
      }
    }
  }
  const sourceText = decodeUtf8(xmlBytes);
  const root = parseXml(sourceText);
  if (!root) {
    return {
      kind: "lbrn",
      fileName,
      layers: [],
      sourceText,
      extractGeometry: () => ({
        geoms: [],
        warnings: [{ kind: "warn", message: "File is not a valid LightBurn project", count: 1 }],
      }),
    };
  }
  const layers = getLayers(root);
  return {
    kind: "lbrn",
    fileName,
    layers,
    sourceText,
    extractGeometry: (selectedLayers, curveSteps) => extractGeometryImpl(root, selectedLayers, curveSteps),
  };
}
